import { NetworkStatus } from 'apollo-client';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import AdvertCard from '@Components/Advert/AdvertCard';
import DocumentTitle from '@Components/DocumentTitle/DocumentTitle';
import AdFilters, { AdFilterFields } from '@Components/forms/Filter/AdFilters';
import AdvertsSortSelect from '@Components/forms/inputs/selects/AdvertsSortSelect';
import Col from '@Components/layout/Col';
import Container from '@Components/layout/Container';
import GridCol from '@Components/layout/Grid/GridCol';
import GridRow from '@Components/layout/Grid/GridRow';
import Layout, { LayoutBackgrounds } from '@Components/layout/Layout';
import Row from '@Components/layout/Row';
import SearchItemsContentLoader from '@Components/Loaders/SearchItemsContentLoader';
import MapPreviewButton from '@Components/MapPreviewButton';
import { showFiltersModal } from '@Components/Modal/modalHelpers';
import PaginatedDataBlock from '@Components/PaginatedDataBlock';
import { useAppKindContext } from '@Components/root';
import Typography from '@Components/Typography';
import { MARKETPLACE_FETCH_LIMIT } from '@Config/constants';
import { Messages } from '@Config/messages';
import {
  GetMarketplaceAdsQuery,
  GetMarketplaceAdsQueryVariables,
  useGetCategoriesLazyQuery,
  useGetMarketplaceAdsQuery,
} from '@Graphql/graphqlTypes.generated';
import { useTranslateObject } from '@Hooks/useTranslateObject';
import { useTranslations } from '@Hooks/useTranslations';
import MarketPlaceFloatingNavigationContent from '@Routes/marketplace/MarketPlacePage/MarketPlaceFloatingNavigationContent';
import { useAdvertFilterData } from '@Utils/hooks/useAdvertFilterData';
import { convertMoneyToCents } from '@Utils/money';
import { defaultSearchValues } from '@Utils/types';

import styles from './MarketplacePage.module.scss';

const defaultFiltersValues: AdFilterFields = {
  priceGte: undefined,
  priceLte: undefined,
  advertType: undefined,
  categories: undefined,
  searchBy: undefined,
};

const MarketplacePage: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const t = useTranslations();
  const selectTranslation = useTranslateObject();
  const { appKind } = useAppKindContext();

  const {
    filterData,
    sortData,
    geoSearchWithDistance,
    searchData: { searchBy },
    filtersInitialValues,
    defaultQuerySortValues,
    onFiltersChange,
    onFiltersClear,
    onSortChange,
  } = useAdvertFilterData();

  const [, { data: categoryData }] = useGetCategoriesLazyQuery({
    fetchPolicy: 'cache-and-network',
    partialRefetch: true,
  });

  const variables = {
    limit: MARKETPLACE_FETCH_LIMIT,
    ...defaultFiltersValues,
    ...defaultSearchValues,
    ...defaultQuerySortValues,
    ...geoSearchWithDistance,
    ...filterData,
    searchBy,
    priceGte: convertMoneyToCents(Number(filterData.priceGte)),
    priceLte: convertMoneyToCents(Number(filterData.priceLte)),
    ...sortData,
    kind: appKind,
  };

  const { loading, data, networkStatus, fetchMore } = useGetMarketplaceAdsQuery({
    variables,
    fetchPolicy: 'cache-and-network',
    partialRefetch: true,
  });

  const filtersModalProps = {
    onFiltersChange,
    onFiltersClear,
    filtersInitialValues,
    showDistanceField: filterData.geoSearch,
    onMobileOnly: true,
  };

  const onFiltersModal = () => {
    dispatch(showFiltersModal(filtersModalProps));
  };

  const renderContent = () => {
    if ([NetworkStatus.loading].includes(networkStatus)) {
      return <SearchItemsContentLoader count={MARKETPLACE_FETCH_LIMIT} gridSize={4} />;
    }

    return (
      <PaginatedDataBlock<GetMarketplaceAdsQuery, GetMarketplaceAdsQueryVariables>
        loading={loading}
        pageInfo={data?.allAdverts?.pageInfo}
        dataLength={data?.allAdverts.edges.length}
        fetchMoreKeyword="allAdverts"
        fetchMore={fetchMore}
        networkStatus={networkStatus}
      >
        <div className={styles.marketplaceContainer}>
          {data?.allAdverts.edges.map(item => <AdvertCard advert={item.node} key={item.node.id} />)}
        </div>
      </PaginatedDataBlock>
    );
  };

  const selectCategoryTranslation = categoryData?.categories[0].parent
    ? categoryData?.categories[0].parent.name
    : categoryData?.categories[0].name;

  const marketplaceTitle = selectCategoryTranslation
    ? `${selectTranslation(selectCategoryTranslation)} - ${t(Messages.marketPlace)}`
    : t(Messages.marketPlace);

  return (
    <DocumentTitle title={marketplaceTitle}>
      <Layout
        background={LayoutBackgrounds.grayBackground}
        floatNavigationContent={<MarketPlaceFloatingNavigationContent onFilterClick={onFiltersModal} />}
      >
        <Container>
          <GridRow className={styles.adFilters}>
            <GridCol size={12}>
              <AdFilters
                onClick={onFiltersClear}
                onChange={onFiltersChange}
                initialValues={filtersInitialValues}
                showDistanceField={filterData.geoSearch}
                submitOnChange
              />
            </GridCol>
          </GridRow>
          <GridRow className={styles.adListGridRowMobile}>
            <GridCol size={12}>
              <div>
                <Col className={styles.resultsTitleBlock}>
                  <Row alignCenter>
                    <Typography tag="h2" size="xl" msg={t(Messages.searchResults)} className={styles.countLabel} />
                    <MapPreviewButton edges={data?.allAdverts.edges.map(edge => edge.node)} filterData={filterData} />
                    <AdvertsSortSelect onChange={onSortChange} value={sortData.orderby} />
                  </Row>
                </Col>
              </div>
              {renderContent()}
            </GridCol>
          </GridRow>
        </Container>
      </Layout>
    </DocumentTitle>
  );
};

export default MarketplacePage;
